import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Home,
  CalendarPlus, 
  Users, 
  LogOut,
  Bell,
  ShieldCheck,
  Menu,
  X,
  MessageSquare,
  MoreHorizontal,
  ClipboardList,
  Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '../ui/sheet';
import PWAInstallBanner from '../PWAInstallBanner';
import TrialCountdownBanner from '../Billing/TrialCountdownBanner';
import Walkthrough from '../Walkthrough/Walkthrough';
import { Logo } from '../Logo';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';

export const ManagerLayout = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Phase 4 UX (Feb 2026): the persistent "Find Cover" FAB navigates to
  // /manager/post-shift itself; hide it while the user is already on the
  // wizard so it doesn't overlap the step Continue / Publish button on
  // small (375px) mobile screens.
  const hideFindCoverFab = location.pathname === '/manager/post-shift';
  const [urgentCount, setUrgentCount] = useState(0);
  const [applicantCount, setApplicantCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Single-source data-walkthrough — see WorkerLayout comment.
  const isLargeScreen = useIsLargeScreen();

  const fetchCounts = useCallback(async () => {
    try {
      const [alertsRes, appRes] = await Promise.all([
        api.get('/notifications/unread-count'),
        api.get('/applications/pending-count')
      ]);
      setUrgentCount(alertsRes.data.urgentCount || 0);
      setApplicantCount(appRes.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch counts:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Phase 1 UX simplification (Feb 2026): 5 primary nav items only.
  // Find Cover is exposed as a prominent secondary CTA (see FAB below).
  // All previous top-level items still exist as routes and are grouped
  // inside /manager/shifts, /manager/staff, /manager/messages or
  // /manager/more so nothing is deleted, just organised.
  const navItems = [
    { to: '/manager', icon: Home, label: 'Home', end: true, walkthroughId: 'wt-mgr-dashboard' },
    { to: '/manager/my-shifts', icon: ClipboardList, label: 'Shifts', walkthroughId: 'wt-mgr-shifts' },
    { to: '/manager/staff', icon: Users, label: 'Staff', walkthroughId: 'wt-mgr-staff', badge: applicantCount },
    { to: '/manager/chat', icon: MessageSquare, label: 'Messages', walkthroughId: 'wt-mgr-messages' },
    { to: '/manager/more', icon: MoreHorizontal, label: 'More', walkthroughId: 'wt-mgr-more', badge: urgentCount },
  ];

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={() => mobile && setMobileMenuOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
          mobile ? 'text-base' : 'text-sm'
        } ${
          isActive
            ? 'bg-white/10 text-white font-semibold'
            : 'text-white/70 hover:bg-white/5 hover:text-white'
        }`
      }
      data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
      data-walkthrough={!mobile && isLargeScreen ? item.walkthroughId : undefined}
    >
      <item.icon className="h-5 w-5" />
      <span>{item.label}</span>
      {item.badge > 0 && (
        <Badge className="ml-auto bg-[#DA291C] text-white text-xs px-2">
          {item.badge}
        </Badge>
      )}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <Walkthrough />
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:flex lg:w-64 lg:flex-col bg-[#0f1b3d]">
        <div className="flex flex-col h-full px-4 py-6">
          {/* Logo */}
          <div className="flex items-center gap-3 px-4 mb-8 shrink-0" data-testid="manager-sidebar-logo">
            <Logo size="sm" variant="light" linkTo="/manager" />
          </div>

          {/* Phase 1 UX: Find Cover — the ONE prominent primary action. */}
          <NavLink
            to="/manager/post-shift"
            className="mx-2 mb-4 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold shadow-lg shadow-[#DA291C]/20 transition-colors text-[15px]"
            data-testid="sidebar-find-cover-cta"
            data-walkthrough={isLargeScreen ? 'wt-mgr-find-cover' : undefined}
          >
            <Search className="h-5 w-5" />
            Find Cover
          </NavLink>

          {/* Navigation - scrollable */}
          <nav className="flex-1 space-y-1 overflow-y-auto min-h-0 scrollbar-thin">
            {navItems.map((item) => (
              <NavItem key={item.to} item={item} />
            ))}
          </nav>

          {/* User section - always visible at bottom */}
          <div className="border-t border-white/10 pt-4 mt-4 shrink-0">
            <div className="px-4 py-3">
              <p className="text-sm font-medium text-white">{user?.fullName}</p>
              <p className="text-xs text-white/60">Manager</p>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/5 px-4"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-[#0f1b3d] text-white">
        <div className="flex items-center justify-between px-4 py-4">
          <Logo size="xs" variant="light" linkTo="/manager" />
          <div className="flex items-center gap-1">
            <NavLink
              to="/manager/alerts"
              aria-label="Alerts"
              className="relative inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg hover:bg-white/10 transition-colors"
              data-testid="mobile-header-alerts-btn"
            >
              <Bell className="h-6 w-6" />
              {urgentCount > 0 && (
                <span className="absolute top-1 right-1 h-5 w-5 bg-[#DA291C] rounded-full flex items-center justify-center text-xs font-bold">
                  {urgentCount}
                </span>
              )}
            </NavLink>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-11 w-11" data-testid="mobile-menu-trigger">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-[#0f1b3d] border-0 p-0">
                <SheetTitle className="sr-only">Manager navigation menu</SheetTitle>
                <SheetDescription className="sr-only">Navigation menu with links to all manager pages</SheetDescription>
                <div className="flex flex-col h-full px-4 py-6">
                  <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="h-7 w-7 text-white" />
                      <span className="text-lg font-bold text-white font-['Public_Sans']">Menu</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-white hover:bg-white/10 h-11 w-11"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-menu-close-btn"
                    >
                      <X className="h-6 w-6" />
                    </Button>
                  </div>
                  <nav className="flex-1 space-y-1 overflow-y-auto min-h-0">
                    {navItems.map((item) => (
                      <NavItem key={item.to} item={item} mobile />
                    ))}
                  </nav>
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <div className="px-4 py-3">
                      <p className="text-sm font-medium text-white">{user?.fullName}</p>
                      <p className="text-xs text-white/60">Manager</p>
                    </div>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/5 px-4 h-12"
                      onClick={handleLogout}
                      data-testid="mobile-menu-signout-btn"
                    >
                      <LogOut className="h-5 w-5" />
                      Sign Out
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="min-h-screen pb-24 lg:pb-0">
          <PWAInstallBanner />
          <TrialCountdownBanner />
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 mobile-nav">
        <div className="flex justify-around items-stretch">
          {navItems.slice(0, 5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-2 px-2 relative min-h-[56px] flex-1 ${
                  isActive ? 'text-[#DA291C]' : 'text-slate-500'
                }`
              }
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              data-walkthrough={!isLargeScreen ? item.walkthroughId : undefined}
            >
              <item.icon className="h-5 w-5" />
              <span className="text-[11px] font-medium leading-tight">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute top-1 right-2 h-4 w-4 bg-[#DA291C] rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Phase 1 UX (Feb 2026): "Find Cover" is the one visually prominent
          primary action for managers — a persistent FAB above the mobile
          bottom-nav (raised via bottom-24) and a red pill on the desktop
          sidebar (rendered inside the sidebar earlier). Uses the existing
          /manager/post-shift route so backend and business logic are
          untouched. */}
      <NavLink
        to="/manager/post-shift"
        className={`lg:hidden fixed bottom-24 right-4 z-50 inline-flex items-center gap-2 h-14 px-5 rounded-full bg-[#DA291C] text-white font-semibold shadow-xl shadow-[#DA291C]/30 hover:bg-[#b91f13] active:scale-95 transition-all text-base ${hideFindCoverFab ? 'hidden' : ''}`}
        data-testid="mobile-find-cover-fab"
        data-walkthrough={!isLargeScreen ? 'wt-mgr-find-cover' : undefined}
        aria-label="Find Cover — post a shift to find someone to cover it"
      >
        <Search className="h-5 w-5" />
        Find Cover
      </NavLink>
    </div>
  );
};

export default ManagerLayout;
